import React, { useContext, useMemo, SetStateAction, Dispatch, useRef, useState, useCallback, useEffect, useImperativeHandle } from "react";
import ROLE from "../../../modules/role";
import Utils from "../../../utils/utils";
import Notifier from "../../../modules/notifier/notifier";
import Debouncer from "../../../modules/debouncer/debouncer";
import AppUserContext from "../../../context/app-user-context";
import { HiExclamationCircle, HiOutlinePlusCircle, HiRefresh } from "react-icons/hi";
import FeedbackItems from "../../../components/portal/FeedbackItems";
import ApiMessage, { TMessageItem } from "../../../modules/api/message";
import PopupSupervisorFeedback, { TPopupSupervisorFeedbackRef} from "../../../components/popup/PopupSupervisorFeedback";
import { MdBorderColor, MdUndo, MdRefresh, MdHistory, MdThumbUp, MdThumbDown } from "react-icons/md";
import ARIAChat from "../../../components/portal/ARIAChat";
import { WLField } from "../../../components/dumb/WLFieldsEditor";
import ModulePopup from "../../../modules/popup/popup";
import { HighlightedSentences } from "../PageMyGroup/PageMyGroupReportWL";
import { FaRegQuestionCircle } from "react-icons/fa";
import Tooltip from "../../../components/common/Tooltip";
import ApiARIAAnalyze from "../../../modules/api/aria-analyze";
import ApiSubmission from "../../../modules/api/submission";
import { SUBMISSION_WL } from "../../../config/constants";
import ApiARIAChat from "../../../modules/api/aria-chat";
import { TFeedbackData } from "../../../modules/popup/FeedbackAlert";
import { motion, AnimatePresence } from "framer-motion";
import ModuleDate from "../../../modules/date";
import { isDebugMode } from "../../../config/config";

// @todo deactivate this feature once live
const ENABLE_REGENERATE = isDebugMode;

interface PageProgressTabReportWLProps {
  submission: any;
  fields: WLField[];
  messages?: TMessageItem[];
  isRefreshingMessages?: boolean;
  refreshMessages?: Dispatch<SetStateAction<boolean>>;
  setMessages?: Dispatch<SetStateAction<TMessageItem[]>>;
  reportTitle?: string;
  loadingAria?: boolean;
  supervisorFeedback?: string;
  additionalPrompt?: string;
  setAdditionalPrompt?: Dispatch<SetStateAction<string>>;
  handleRegenerateAnalysis?: () => void;
  handleRegenerateFeedback?: () => void;
  highlightedSentences?: HighlightedSentences;
  setHighlightedSentences?: Dispatch<SetStateAction<HighlightedSentences>>;
  report_summary?: string;
  sentimentIcon?: string;
  sentimentClass?: string;
  sentiment?: string;
  sentiment_analysis_rationale?: JSX.Element[];
  supervisor_recommendation?: string;
  feedbackSubmissionDate?: string;
  report_too_short?: boolean;
}

type TFeedbackFields = {
  messageId: string;
  feedback: string;
  score: number|string;
};

const PageProgressTabReportWL: React.FC<PageProgressTabReportWLProps> = ({
  submission,
  fields,
  messages,
  isRefreshingMessages,
  refreshMessages,
  setMessages,
  reportTitle,
  loadingAria,
  supervisorFeedback,
  additionalPrompt,
  setAdditionalPrompt,
  handleRegenerateFeedback,
  handleRegenerateAnalysis,
  highlightedSentences,
  setHighlightedSentences,
  report_too_short = false,
  report_summary,
  sentimentIcon,
  sentimentClass,
  sentiment,
  sentiment_analysis_rationale,
  supervisor_recommendation,
  feedbackSubmissionDate,
}) => {
  const user = useContext(AppUserContext);
  const isSupervisor = useMemo(() => user.role === ROLE.SUPERVISOR, [user]);
  const isStudent = useMemo(() => user.role === ROLE.STUDENT, [user]);
  const refPopupFeedback = useRef<TPopupSupervisorFeedbackRef>();
  const content = JSON.parse(submission.content ?? "{}");
  const [isHighlighting, setIsHighlighting] = useState(false);
  const historyRef = useRef<HTMLDivElement>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [processedReports, setProcessedReports] = useState([]);
  const [showAriaRatingModal, setShowAriaRatingModal] = useState<boolean>(true);
  const [rating, setRating] = useState<'like' | 'dislike' | null>(null);
  const [comment, setComment] = useState<string>('');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const historyDummyData = [
    {
      week: "Week 5. January 31, 2025",
      content: [
        "I supported an international airport project, evaluating carpark systems to enhance revenue, customer experience, and operational efficiency.",
        "Conducted a comparative analysis of two carpark proposals, focusing on industry experience, pricing, capacity management, and payment integration.",
        "Performed financial analysis using Excel, compared key metrics, and presented findings in PowerPoint, highlighting the pros and cons of each proposal.",
        "Identified the optimal system with advanced technologies like ANPR and mobile payments, aligning with the airport's goals. Presented the analysis to the Director for review.",
        "The task improved my analytical, technical, and presentation skills, showing the importance of critical thinking and data-driven decisions in achieving long-term financial objectives.",
      ],
    },
    {
      week: "Week 5. January 31, 2025",
      content: [
        "I supported an international airport project, evaluating carpark systems to enhance revenue, customer experience, and operational efficiency.",
        "Conducted a comparative analysis of two carpark proposals, focusing on industry experience, pricing, capacity management, and payment integration.",
        "Performed financial analysis using Excel, compared key metrics, and presented findings in PowerPoint, highlighting the pros and cons of each proposal.",
        "Identified the optimal system with advanced technologies like ANPR and mobile payments, aligning with the airport's goals. Presented the analysis to the Director for review.",
        "The task improved my analytical, technical, and presentation skills, showing the importance of critical thinking and data-driven decisions in achieving long-term financial objectives.",
      ],
    },
  ];

  const refInputScore = useRef<HTMLInputElement>();

  const [tooltip, setTooltip] = useState({
    content: null,
    position: null,
    closeButton: null,
  });
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [formData, setFormData] = useState<TFeedbackFields>({
    messageId: "",
    feedback: "",
    score: "",
  });
  const [disabled, setDisabled] = useState(false);
  const [feedbackSenderName, setFeedbackSenderName] = useState("");

  const [ratingFormData, setRatingFormData] = useState<{
    ratingId?: string;
    comment: string;
    isLiked: boolean;
  } | null>(null);
  const [ratingFormDisabled, setRatingFormDisabled] = useState(false);
  const [ratingSenderName, setRatingSenderName] = useState("");
  const [ratingSenderDate, setRatingSenderDate] = useState("");

  const handleRatingSelect = (selectedRating: 'like' | 'dislike') => {
    if (ratingFormDisabled) return;

    setRatingFormData((prev) => {
      const currentRating = prev?.isLiked ? 'like' : prev?.isLiked === false ? 'dislike' : null;

      if (currentRating === selectedRating) {
        return { ...prev, isLiked: null };
      }

      return { ...prev, isLiked: selectedRating === 'like' };
    });
  };

  const handleWriteFeedbackClick = (e: any) => {
    e.preventDefault();
    let opts: any;
    const existingMessage = messages.filter(
      (f) => f.senderId === user.referenceUserId
    );
    if (existingMessage.length > 0) {
      opts = {
        messageId: existingMessage[0]._id,
        feedback: existingMessage[0].message,
      };
    }
    refPopupFeedback.current.show(opts);
  };

  const supervisorActions = useMemo(() => {
    if (!user || !refPopupFeedback) return <></>;

    return (
      <div className="tf-actions">
        <button
          className="btn btn-icon btn-secondary"
          onClick={handleWriteFeedbackClick}
        >
          Write Feedback <HiOutlinePlusCircle />
        </button>
      </div>
    );
  }, [refPopupFeedback, user, submission, messages]);

  const handleTextSelection = useCallback(
    (e: any) => {
      if (!isHighlighting) return;

      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0 || selection.toString().trim() === "") return;

      const range = selection.getRangeAt(0);
      const parentElement = range.startContainer.parentElement;

      const fieldElement = e.currentTarget;
      if (!fieldElement.contains(parentElement)) return;

      const fieldId = fieldElement.getAttribute("data-field-id");
      if (!fieldId) return;

      const selectedText = selection.toString();
      if (!selectedText) return;

      const highlightId = `${fieldId}-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;

      try {
        const mark = document.createElement("mark");
        mark.className = "highlighted-text";
        mark.setAttribute("data-field-id", fieldId);
        mark.setAttribute("data-highlight-id", highlightId);
        mark.textContent = selectedText;

        range.surroundContents(mark);
        selection.removeAllRanges();

        setHighlightedSentences((prev) => {
          const updated = {
            ...prev,
            [fieldId]: [...(prev[fieldId] || []), { text: selectedText, id: highlightId }],
          };
          const storageKey = getHighlightStorageKey(user.id, submission._id);
          localStorage.setItem(storageKey, JSON.stringify(updated));
          return updated;
        });
      } catch (error) {
        console.error("Error highlighting text:", error);
        selection.removeAllRanges();
      }
    },
    [isHighlighting, highlightedSentences]
  );

  const toggleHighlight = useCallback(() => {
    if (isHighlighting) {
      // alert(`Highlighting completed! ${highlightedSentences.length} sentences highlighted.`);
      // console.log(highlightedSentences);
    }
    setIsHighlighting(!isHighlighting);
  }, [isHighlighting, highlightedSentences]);

  const handleUndo = useCallback(() => {
    const fieldIds = Object.keys(highlightedSentences);
    if (fieldIds.length === 0) return;

    const lastFieldId = fieldIds[fieldIds.length - 1];
    const lastFieldHighlights = highlightedSentences[lastFieldId];

    if (!lastFieldHighlights || lastFieldHighlights.length === 0) return;

    const { text, id } = lastFieldHighlights[lastFieldHighlights.length - 1];

    const mark = document.querySelector(`mark[data-highlight-id="${id}"]`);
    if (mark) {
      const parent = mark.parentNode;
      if (parent) {
        parent.replaceChild(document.createTextNode(text), mark);
      }
    }

    setHighlightedSentences((prev) => {
      const updated = { ...prev };
      updated[lastFieldId] = updated[lastFieldId].slice(0, -1);
      if (updated[lastFieldId].length === 0) {
        delete updated[lastFieldId];
      }
      const storageKey = getHighlightStorageKey(user.id, submission._id);
      localStorage.setItem(storageKey, JSON.stringify(updated));
      return updated;
    });
  }, [highlightedSentences]);

  const handleReset = useCallback(() => {
    document.querySelectorAll("mark.highlighted-text").forEach((mark) => {
      const text = document.createTextNode(mark.textContent || "");
      mark.replaceWith(text);
    });

    setHighlightedSentences({});
    const storageKey = getHighlightStorageKey(user.id, submission._id);
    localStorage.removeItem(storageKey);
  }, []);

  const handleOnChange = (e: any) => {
    setFormData((v) => ({ ...v, [e.target.name]: e.target.value }));
  };

  const validate = () => {
    let valid = true;
    if (formData.feedback === "") {
      valid = false;
      Notifier.error("Feedback is required");
    }

    const strScore = typeof formData.score === "string" ? formData.score.trim() : formData.score;
    const score = Number(strScore);
    if (valid && (isNaN(score) || score < 1 || score > 10)) {
      Notifier.error("Score must be between 1 and 10.");
      valid = false;
    }

    return valid;
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    const isDraft = e.target.name === "saveAsDraft";

    const strScore = typeof formData.score === "string" ? formData.score.trim() : formData.score;
    const score = Number(strScore);
    const showScorePopupDialogue = strScore === "" || isNaN(score) || score < 1 || score > 10;

    if (!isDraft && showScorePopupDialogue) {
      ModulePopup.showAlert({
        title: (
          <>
            <HiExclamationCircle /> Alert!
          </>
        ),
        description: "Please give the Report a score before submitting your feedback.",
        labelOK: "Okay",
        async onOK() {
          await Utils.sleep(350);
          setFormData(f => ({ ...f, score: "" }));
          refInputScore.current.focus();
        }
      });
      return;
    } else {
      if (!validate()) return;
      ModulePopup.showAlertConfirm({
        description: isDraft ? "Do you want to save your draft?" : "Are you sure you want to submit?",
        async onYes() {
          submitFeedback(isDraft);
        },
      });
    }
  };

  const submitFeedback = useCallback((isDraft: boolean) => {
    Debouncer.execute("SUBMIT_FEEDBACK", async () => {
      const isEditMode = formData.messageId !== "";
      !isDraft && setDisabled(true);
      let res: any;

      if (!isEditMode) {
        res = await ApiMessage.submit({
          createdUserId: user.id,
          messageType: "SIT SUPERVISOR",
          message: formData.feedback,
          traitId: undefined,
          receiverId: submission.studentId,
          senderId: user.referenceUserId,
          score: formData.score,
          status: isDraft ? "Draft" : "Unread",
          submissionId: submission._id,
        });
      } else {
        res = await ApiMessage.update(formData.messageId, {
          message: formData.feedback,
          score: formData.score,
          status: isDraft ? "Draft" : "Unread",
        });
      }

      let submitError = false;

      if (res.status) {
        if (isEditMode) {
          if (isDraft) {
            Notifier.success("Feedback saved");
          } else {
            Notifier.success("Feedback updated");
          }

          setMessages((messages) => {
            return messages.map((message) => {
              if (message._id === formData.messageId) {
                message.message = formData.feedback;
                message.score = formData.score;
                message.status = isDraft ? "Draft" : "Unread";
              }

              return message;
            });
          });
        } else {
          if (isDraft) {
            Notifier.success("Feedback saved");
          } else {
            Notifier.success("Feedback submitted");
          }

          Utils.sleep(1000).then(() => refreshMessages((v) => !v));
        }

        refPopupFeedback.current.hide();
      } else {
        submitError = true;
        Notifier.error(res.message);
      }

      isDraft && setDisabled(false);

      if (
        (isDraft) ||
        (!isDraft && submitError)
      ) {
        setDisabled(false);
      }
    });
  }, [user, submission, setMessages, refreshMessages, formData]);

  const loadSavedHighlights = useCallback(() => {
    const storageKey = getHighlightStorageKey(user.id, submission._id);
    const savedData = localStorage.getItem(storageKey);
    if (!savedData) return;

    const highlights: HighlightedSentences = JSON.parse(savedData);
    setHighlightedSentences(highlights);

    Object.entries(highlights).forEach(([fieldId, sentences]) => {
      const fieldElement = document.querySelector(`[data-field-id="${fieldId}"]`);
      if (!fieldElement) return;

      sentences.forEach(({ text, id }) => {
        const innerHTML = fieldElement.innerHTML;
        const regex = new RegExp(`(${text})`, "i");
        fieldElement.innerHTML = innerHTML.replace(
          regex,
          `<mark class="highlighted-text" data-field-id="${fieldId}" data-highlight-id="${id}">$1</mark>`
        );
      });
    });
  }, []);

  const getHighlightStorageKey = (userId: string, submissionId: string): string => {
    return `highlight_${userId}_${submissionId}`;
  };

  const handleMouseEnter = (e: any, content: any, closeButton = true) => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    const rect = e.target.getBoundingClientRect();
    setTooltip({
      content,
      position: {
        top: rect.top + window.scrollY - 10,
        left: rect.left + window.scrollX + rect.width / 2,
      },
      closeButton,
    });
  };

  const handleMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setTooltip({ content: null, position: null, closeButton: null });
    }, 100);
  };

  const handleTooltipMouseEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const handleTooltipMouseLeave = () => {
    setTooltip({ content: null, position: null, closeButton: null });
  };

  const getSentimentEmoji = (type) => {
    switch (type) {
      case "Positive":
        return "/assets/images/icon/icon-reaction-good.svg";
      case "Neutral":
        return "/assets/images/icon/icon-reaction-neutral.svg";
      case "Negative":
        return "/assets/images/icon/icon-reaction-mad.svg";
      default:
        return "";
    }
  };

  const formatSentencesWithBreaks = (text: string) => {
    if (!text) return null;

    return text
      .split(/(?<=[.?!])\s+(?=[A-Z])/)
      .reduce<JSX.Element[]>((acc, sentence, index, array) => {
        acc.push(
          <span key={`sentence-${index}`}>
            {sentence + ' '}
          </span>
        );

        const isNotLast = index < array.length - 1;
        if ((index + 1) % 2 === 0 && isNotLast) {
          acc.push(<span key={`br-${index}`} style={{ display: 'block', height: '10px' }} />);
        }

        return acc;
      }, []);
  };

  const openReportHistory = () => {
    ModulePopup.showAlertInfo({
      title: (
        <>
          <MdHistory color="black" /> Workplace Learning Report History
        </>
      ),
      size: "xl",
      description: (
        <div className="report-history" ref={historyRef}>
          {loadingHistory ? (
            <div className="loading-indicator">Loading history data...</div>
          ) : processedReports.length > 0 ? (
            processedReports.map((entry) => (
              <div key={entry.reportNumber} className="report-item">
                <strong
                  className="report-title"
                  title={`Submitted on: ${new Date(entry.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}`}
                >
                  Report {entry.reportNumber}, {new Date(entry.deadlineDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </strong>
                <div className="report-entry">
                  {entry.result?.content?.sentiment_analysis?.sentiment && (
                    <div className="sentiment-item">
                      Sentiment:{" "}
                      <span className={`sentiment-icon ${entry.result.content.sentiment_analysis.sentiment ?? 'pending'}`}></span>
                      {/* <img
                        src={getSentimentEmoji(entry.result.content.sentiment_analysis.sentiment)}
                        alt={entry.result.content.sentiment_analysis.sentiment}
                        className="sentiment-icon"
                      /> */}
                    </div>
                  )}
                  {entry.result?.content?.report_summary && (
                    <div className="summary-item">
                      {formatSentencesWithBreaks(entry.result.content.report_summary)}
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="no-history">No processed reports available</div>
          )}
        </div>
      ),
    });

    setTimeout(() => {
      if (historyRef.current) {
        historyRef.current.scrollTo({
          top: historyRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    }, 150);
  };

  const handleAriaRatingModal = (like: boolean) => {
    ModulePopup.showFeedbackAlert({
      title: "Rate ARIA’s Summary",
      description: "How would you rate your experience with ARIA?",
      buttonLabel: "Send Feedback",
      defaultRating: like ? "like" : "dislike",
      onSubmit: async (feedback) => {
        const rateAriaChat = await ApiARIAChat.rateARIAChat(user.id, submission._id, feedback.comment, feedback.isLiked, user.role);
        if (rateAriaChat.status) {
          Notifier.success("Feedback submitted. Thank you!");
        } else {
          Notifier.error("An error occurred");
        }
        setShowAriaRatingModal(false);
      },
    });
  };

  const handleSubmitRating = () => {
    if (ratingFormData?.isLiked !== null && ratingFormData?.isLiked !== undefined) {
      const feedbackData: TFeedbackData = {
        isLiked: ratingFormData.isLiked,
        rating: ratingFormData.isLiked ? 'like' : 'dislike',
        comment: comment
      };
      onSubmitRating(feedbackData);
    }

    setTimeout(async () => {
      setRatingFormDisabled(true);
      await getSubmissionRating();
      // setComment('');
      // setRatingFormData(null);
    }, 300);
  };

  const onSubmitRating = async (feedback) => {
    const rateAriaChat = await ApiARIAChat.rateARIAChat(user.id, submission._id, feedback.comment, feedback.isLiked, user.role);
    if (rateAriaChat.status) {
      Notifier.success("Rating submitted. Thank you!");
    } else {
      Notifier.error("An error occurred");
    }
    // setShowAriaRatingModal(false);
  }

  useEffect(() => {
    const fetchSubmissionHistory = async () => {
      if (!submission || !submission.studentId) return;

      setLoadingHistory(true);
      try {
        const responseGrowPro = await ApiSubmission.submissionHistory(
          submission.studentId,
          SUBMISSION_WL,
          submission.cohort?._id || ""
        );

        const submittedReportIds = responseGrowPro
          .filter(report => report.submission)
          .map(report => report.submission._id);

        const userId = submission.createdUserId;
        const response = await ApiARIAAnalyze.getSubmissionAnalysisHistory(
          userId,
          submittedReportIds
        );

        if (response.status && response.data) {
          const analysisMap = new Map(
            response.data.map(item => [item.submissionId, item])
          );

          const merged = responseGrowPro
            .map((report, index) => {
              const submissionId = report.submission?._id;
              const matchedAnalysis = submissionId ? analysisMap.get(submissionId) : null;

              if (!matchedAnalysis) return null;

              return {
                ...(typeof matchedAnalysis === 'object' && matchedAnalysis !== null ? matchedAnalysis : {}),
                reportNumber: index + 1,
                deadlineDate: report.to,
                createdAt: report.submission?.createdDate
              };
            })
            .filter(Boolean);

          setProcessedReports(merged);
        } else {
          console.error("Failed to fetch analysis history:", response.message);
          setProcessedReports([]);
        }
      } catch (error) {
        console.error("Error fetching submission history:", error);
        setProcessedReports([]);
      } finally {
        setLoadingHistory(false);
      }
    };

    fetchSubmissionHistory();
  }, [submission]);

  // useEffect(() => {
  //   loadSavedHighlights();
  // }, []);

  const getSubmissionRating = async () => {
    if (submission) {
      const allAriaRating = await ApiARIAChat.getAriaAllRatingBySubmissionId(submission._id);
      let supervisorOnlyRating: any = [];

      if (
        allAriaRating.status && allAriaRating.data.length > 0 &&
        (supervisorOnlyRating = allAriaRating.data.filter((r) => r.source !== ROLE.STUDENT)) &&
        supervisorOnlyRating.length > 0
      ) {
        const latestRating = supervisorOnlyRating.sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )[0];

        setRatingFormData({
          ratingId: latestRating._id,
          comment: latestRating.feedback,
          isLiked: latestRating.like
        });

        setRatingSenderName(latestRating?.userInfo?.name);
        setRatingSenderDate(ModuleDate.getSubmissionDateFormat(latestRating?.createdAt, true));
        setRatingFormDisabled(true);
      } else {
        setRatingFormDisabled(false);
        setRatingSenderName("");
        setRatingSenderDate("");
      }
    }
  };

  useEffect(() => {
    getSubmissionRating();
  }, [submission]);

  useEffect(() => {
    if (messages && messages.length > 0) {
      const existingMessage = messages.filter(
        (f) => f.senderId === user.referenceUserId
      );

      const nonDraftMessages = messages.filter((m) => m.status !== "Draft");

      if (nonDraftMessages.length > 0) {
        const latestNonDraft = nonDraftMessages.sort(
          (a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
        )[0];

        setFormData({
          messageId: latestNonDraft._id,
          feedback: latestNonDraft.message,
          score: latestNonDraft.score
        });

        setFeedbackSenderName(latestNonDraft.senderName);
        setDisabled(true);
      } else if (existingMessage.length > 0) {
        setFormData({
          messageId: existingMessage[0]._id,
          feedback: existingMessage[0].message,
          score: existingMessage[0].score
        });

        setFeedbackSenderName("");
        setDisabled(false);
      } else {
        setDisabled(false);
        setFeedbackSenderName("");
      }
    } else {
      setDisabled(false);
      setFeedbackSenderName("");
    }
  }, [messages, user.referenceUserId]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [ratingFormData?.isLiked]);

  return (
    <>
      {isSupervisor && (
        <PopupSupervisorFeedback
          ref={refPopupFeedback}
          submission={submission}
          onSubmit={(formData, setDisabled, isDraft) => {
            Debouncer.execute("SUBMIT_FEEDBACK", async () => {
              const isEditMode = formData.messageId !== "";
              setDisabled(true);
              let res: any;

              if (!isEditMode) {
                res = await ApiMessage.submit({
                  createdUserId: user.id,
                  messageType: "SIT SUPERVISOR",
                  message: formData.feedback,
                  traitId: undefined,
                  receiverId: submission.studentId,
                  senderId: user.referenceUserId,
                  score: formData.score,
                  status: isDraft ? "Draft" : "Unread",
                  submissionId: submission._id,
                });
              } else {
                res = await ApiMessage.update(formData.messageId, {
                  message: formData.feedback,
                  score: formData.score,
                  status: isDraft ? "Draft" : "Unread",
                });
              }

              if (res.status) {
                if (isEditMode) {
                  if (isDraft) {
                    Notifier.success("Feedback saved");
                  } else {
                    Notifier.success("Feedback updated");
                  }

                  setMessages((messages) => {
                    return messages.map((message) => {
                      if (message._id === formData.messageId) {
                        message.message = formData.feedback;
                        message.score = formData.score;
                        message.status = isDraft ? "Draft" : "Unread";
                      }

                      return message;
                    });
                  });
                } else {
                  if (isDraft) {
                    Notifier.success("Feedback saved");
                  } else {
                    Notifier.success("Feedback submitted");
                  }

                  Utils.sleep(1000).then(() => refreshMessages((v) => !v));
                }

                refPopupFeedback.current.hide();
              } else {
                Notifier.error(res.message);
              }

              // setDisabled(false);
            });
          }}
        />
      )}

      {isSupervisor &&
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div></div>
          <h1 className="fs-title d-none">
            {reportTitle}
          </h1>
          {/* <div className="highlight-controls">
            <button
              className="highlight-btn highlight-btn-default"
              onClick={handleReset}
              disabled={Object.keys(highlightedSentences).length === 0}
              >
              <MdRefresh className="highlight-icon" />
              Reset
            </button>
            <button
              className="highlight-btn highlight-btn-default"
              onClick={handleUndo}
              disabled={Object.keys(highlightedSentences).length === 0}
              >
              <MdUndo className="highlight-icon" />
              Undo
            </button>
            <button
              className={`highlight-btn ${isHighlighting ? 'highlight-btn-warning' : 'highlight-btn-default'}`}
              onClick={toggleHighlight}
            >
              <MdBorderColor className="highlight-icon" />
              {isHighlighting ? 'Finish Highlighting' : 'Start Highlighting'}
            </button>
          </div> */}
          <button
            className="btn btn-sm btn-secondary"
            onClick={openReportHistory}>
            <MdHistory className="report-history-icon" /> Report History
          </button>
        </div>
      }

      <div className="report-info-wrap" data-comp="PageProgressTabReportWL">
        {fields.length ? (
          fields.map((field, i) => (
            <React.Fragment key={i}>
              <div className="report-info">
                <h1>
                  <strong>{field.name}</strong>
                </h1>
                {field.description && <div className="report-info__desc">{field.description}</div>}
                <div
                  className="report-info__content"
                  dangerouslySetInnerHTML={{
                    __html: content[field._id] ?? "",
                  }}
                  data-field-id={field._id}
                  onMouseUp={handleTextSelection}
                  onTouchEnd={handleTextSelection}
                ></div>
              </div>
              {i < fields.length - 1 && <hr />}
            </React.Fragment>
          ))
        ) : (
          <>
            <div className="report-info">
              <h1>
                <strong>Summary of Key Activities</strong>
              </h1>
              <div
                className="report-info__content"
                dangerouslySetInnerHTML={{
                  __html: submission?.summaryKeyActivities ?? "",
                  }}
                data-field-id={"summaryKeyActivities"}
                onMouseUp={handleTextSelection}
                onTouchEnd={handleTextSelection}
              ></div>
            </div>

            <hr />

            <div className="report-info">
              <h1>
                <strong>Details of Learning</strong>
              </h1>
              <div
                className="report-info__content"
                dangerouslySetInnerHTML={{
                  __html: submission?.detailsLearning ?? "",
                }}
                data-field-id={"detailsLearning"}
                onMouseUp={handleTextSelection}
                onTouchEnd={handleTextSelection}
              ></div>
            </div>

            <hr />

            <div className="report-info">
              <h1>
                <strong>Reflection</strong>
              </h1>
              <div
                className="report-info__content"
                dangerouslySetInnerHTML={{
                  __html: submission?.reflection ?? "",
                }}
                data-field-id={"reflection"}
                onMouseUp={handleTextSelection}
                onTouchEnd={handleTextSelection}
              ></div>
            </div>
          </>
        )}
      </div>

      {
        <div className="trait-feedback" id={`feedback-${submission._id}`}>
          {/* <h1>Feedbacks</h1>
          <div className="tf__actions">
            <button
              className="btn btn-primary"
              disabled={isRefreshingMessages}
              onClick={(e) => {
                e.preventDefault();
                refreshMessages((v) => !v);
              }}
            >
              <HiRefresh /> Refresh
            </button>
          </div> */}
          <div
            className={`feedback-list-wrapper ${
              isRefreshingMessages ? "flw__loading" : ""
            }`}
          >
            {/* sit supervisor */}
            {isSupervisor &&
              <>
                <div className="sit_supervisor">
                  {/* <div className="aria__wrapper">
                    <ARIAChat is_supervisor_summary={true}
                        show_close_button={false}
                        supervisor_summary={supervisorFeedback ?? ""}
                        custom_desc="Proposed Feedback from ARIA"
                        loading={loadingAria}
                        additionalPrompt={additionalPrompt}
                        setAdditionalPrompt={setAdditionalPrompt}
                        handleRegenerateFeedback={handleRegenerateFeedback}

                    />
                  </div> */}
                  {/* ARIA Review */}
                  <div className="mgr__aria-summary">
                    <div className="mgr__aria-summary__header">
                      <h1>Analysis by ARIA</h1>
                    </div>
                    {!loadingAria ?
                        <div className="mgr__aria-summary__details">
                          {report_too_short && <>
                            <div className="mgr__aria-summary__item">
                              <p className="my-2">The student's report is too short to be analysed.</p>
                            </div>
                          </>}
                          {!report_too_short && <>
                            <div className="mgr__aria-summary__item">
                              <h1>Summary of the Workplace Learning Report</h1>
                              <p>
                              {report_summary}
                              </p>
                            </div>
                            <div className="mgr__aria-summary__item">
                              <h1>
                                Sentiment Analysis of the Report : <span className={`sentiment-icon ${sentimentClass}`}></span>
                                {/* <img src={sentimentIcon} alt="" /> */}
                                <span className={sentiment?.toLowerCase()}>{sentiment}</span>
                              </h1>
                              <ul>
                                {sentiment_analysis_rationale}
                              </ul>
                            </div>
                            <div className="mgr__aria-summary__item">
                              <h1>Suggested Feedback for the Student</h1>
                              <p>
                                {formatSentencesWithBreaks(supervisor_recommendation)}
                              </p>
                            </div>
                          </>}
                        </div>
                        :
                        <div className='ac__chat__typing'>
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                      }
                    {ENABLE_REGENERATE && <div className="d-flex justify-content-center my-3">
                      <span onClick={() => handleRegenerateAnalysis()} style={{cursor: "pointer"}}><MdRefresh /> Generate again</span>
                    </div>}
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <label htmlFor="score">Your Feedback to the Student</label>
                  </div>
                  {!disabled && <div className="alert alert-info mb-0 mt-0">
                    Please note that changes cannot be made after you have submitted the feedback.
                  </div>}
                  {/* {supervisorActions} */}
                  <textarea name="feedback" value={formData.feedback} id="sit_supervisor" placeholder="Write your feedback" rows={5} onChange={handleOnChange} disabled={disabled}>
                    {formData.feedback}
                  </textarea>
                </div>
                <div className="sit_supervisor-score">
                  <label htmlFor="score">Your score for this Workplace Learning Report submission</label>
                    <input type="text" style={{ maxWidth: "250px" }} min={0} max={10} placeholder="Enter a value between 1-10" name="score" value={formData.score} onChange={handleOnChange} disabled={disabled} ref={refInputScore} />
                </div>
                <div className="sit_supervisor-buttons">
                  <button type="button" className='mt-1 btn btn-secondary btn-mw-centered'
                    onClick={handleSubmit} disabled={disabled}
                  >Submit</button>
                  <button type="button" className='mt-1 btn btn-outline-secondary btn-mw-centered'
                    onClick={handleSubmit} disabled={disabled} name="saveAsDraft"
                  >Save Draft</button>
                </div>
                {disabled && <div className="alert alert-info">
                Feedback submitted by {feedbackSenderName} to the student on {feedbackSubmissionDate}
                </div>}

              {showAriaRatingModal &&
                <>
                  <div className="mgr__aria-summary__header__buttons">
                    <h1>Rate ARIA’s Analysis</h1>
                    <button
                      className={`rating-button ${ratingFormData?.isLiked === true ? 'selected' : ''}`}
                      onClick={() => handleRatingSelect('like')}
                      disabled={ratingFormDisabled}
                    >
                      <div className={`icon-container thumb-up ${ratingFormData?.isLiked === false ? 'disabled' : ''}`}>
                        <span><MdThumbUp /></span>
                      </div>
                    </button>

                    <button
                      className={`rating-button ${ratingFormData?.isLiked === false ? 'selected' : ''}`}
                      onClick={() => handleRatingSelect('dislike')}
                      disabled={ratingFormDisabled}
                    >
                      <div className={`icon-container thumb-down ${ratingFormData?.isLiked === true ? 'disabled' : ''}`}>
                        <span><MdThumbDown /></span>
                      </div>
                    </button>
                </div>
                <AnimatePresence>
                  {ratingFormData?.isLiked !== null && ratingFormData?.isLiked !== undefined && (
                    <motion.div
                      className="mgr__aria-summary__rating"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.4 }}
                    >
                      <div className="feedback-input">
                        <label>Please let us know if you have any additional feedback (optional)</label>
                        <textarea
                          ref={textareaRef}
                          rows={4}
                          value={ratingFormDisabled ? ratingFormData?.comment : comment}
                          onChange={(e) => setComment(e.target.value)}
                          placeholder="Share your thoughts..."
                          disabled={ratingFormDisabled}
                        />
                      </div>
                      {!ratingFormDisabled && (
                        <button
                          className="btn btn-mw btn-secondary mb-2"
                          onClick={handleSubmitRating}
                          disabled={ratingFormDisabled}
                        >
                          Submit Your Rating
                        </button>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
                </>
              }

              {ratingFormDisabled  && <div className="alert alert-success">
                Rating submitted by {ratingSenderName} to the ARIA on {ratingSenderDate}
              </div>}
              </>
            }
            {isStudent &&
              <div className="trait-feedback" id={`feedback-${submission._id}`}>
              <h1>Feedback from your Supervisor</h1>
              {/* <div className="tf__actions">
                <button
                  className="btn btn-primary"
                  disabled={isRefreshingMessages}
                  onClick={(e) => {
                    e.preventDefault();
                    refreshMessages((v) => !v);
                  }}
                >
                  <HiRefresh /> Refresh
                </button>
              </div> */}
              <div
                className={`feedback-list-wrapper ${
                  isRefreshingMessages ? "flw__loading" : ""
                }`}
              >
                <FeedbackItems
                  hideEditButton={true}
                  messages={messages}
                  labelUnavailable="Pending"
                  refreshMessages={refreshMessages}
                  onClickEdit={handleWriteFeedbackClick}
                  hideTraitLabel
                />
              </div>
            </div>
            }
          </div>
        </div>
      }
      <Tooltip
        content={tooltip.content}
        position={tooltip.position}
        closeButton={tooltip.closeButton}
        onClose={handleTooltipMouseLeave}
        onMouseEnter={handleTooltipMouseEnter}
        onMouseLeave={handleTooltipMouseLeave}
      />
    </>
  );
};

export default PageProgressTabReportWL;
